import { useCallback } from 'react';

import { FormInputProps } from '@occc-app/design-system';
import { LazyAutocompleteField } from '@occc/ui-design-system';
import { Audience_Status, AudienceFilter, AudienceSelectItemFragment } from '@occc/graphql';
import { useChainId } from '@occc-app/Chain/hooks';
import { useGetAudienceSelectItemsLazy } from '@occc-app/dataAccessLayer/audience/queries';

export type AudiencesSelectFieldProps = {
  name: string;
  inputProps?: FormInputProps;
  uuidsToExclude?: string[];
};

const mapQueryResponseToRawArray = (
  response: ReturnType<typeof useGetAudienceSelectItemsLazy>['response'],
) => response.data || [];

const mapRawResponseItemToAutocompleteOption = (audience: AudienceSelectItemFragment) => ({
  label: audience.name,
  value: audience.uuid,
  _raw: audience,
});

export const AudiencesSelectField = ({
  name,
  inputProps,
  uuidsToExclude,
}: AudiencesSelectFieldProps) => {
  const chainId = useChainId();

  const makeQueryFilter = useCallback(
    (inputValue: string) => {
      const filter: AudienceFilter = {
        search: inputValue ? inputValue : undefined,
        chainIdEquals: chainId,
        statusContains: [Audience_Status.Active],
        excludeAggregatedAudiences: true,
        uuidNotContains: uuidsToExclude,
      };

      return filter;
    },
    [uuidsToExclude, chainId],
  );

  return (
    <LazyAutocompleteField
      name={name}
      label='Select Audiences'
      inputProps={inputProps}
      useLazyQuery={useGetAudienceSelectItemsLazy}
      makeQueryFilter={makeQueryFilter}
      mapQueryResponseToRawArray={mapQueryResponseToRawArray}
      mapRawResponseItemToAutocompleteOption={mapRawResponseItemToAutocompleteOption}
      filterSelectedOptions
    />
  );
};
